<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { useTranslation } from 'i18next-vue'
import { computed } from 'vue'
import MegaMenu from 'primevue/megamenu'
import Logo from '@/components/Logo.vue'
import NavMenuButtonIcon from '@/components/navbar/NavMenuButtonIcon.vue'
import NavMenuItem from '@/components/navbar/NavMenuItem.vue'
import NavBarActions from '@/components/navbar/NavBarActions.vue'
import '@kern-ux/native/dist/js/kern-kopfzeile.js'
import '@kern-ux/native/dist/fonts/fira-sans.css'
import { useMenuItems } from '@/components/navbar/useMenuItems'
import type { MenuItemBindProps } from '@/types/menu'

const { t } = useTranslation()

// MegaMenu switches to mobile layout below Tailwind's `lg` breakpoint (1024px)
const mobileBreakpoint = `${breakpointsTailwind.lg - 1}px`
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('lg')
withDefaults(
    defineProps<{
        hideNavItems?: boolean
    }>(),
    {
        hideNavItems: false,
    }
)

const { responsiveMenuItems } = useMenuItems()

const megaMenuPt = computed(() => ({
    root: 'border-0 md:px-10 md:py-4 z-2',
    button: {
        class: 'order-0 lg:order-1 text-primary w-8 h-8 max-md:grid',
        'data-testid': 'nav-bar-menu-button',
        ...(isMobile.value ? { 'aria-label': t('frontend.navbar.toggleMenu') } : {}),
    },
    start: 'order-1 lg:order-0',
    end: 'order-3',
    overlay: 'md:min-w-120 md:my-8 md:mx-10',
    submenu: 'gap-2',
    submenulabel: 'hidden',
    column: 'p-3',
    rootlist: 'max-sm:space-y-4 max-h-[max-content]!',
    item: ({ context }: { context: { item?: { isColumnHeader?: boolean } } }) => ({
        class: context.item?.isColumnHeader ? 'pointer-events-none' : '',
    }),
    itemlink: 'flex items-start',
    itemcontent: 'grid',
}))
</script>

<template>
    <div>
        <kern-kopfzeile breakpoints="0px:calc(100% - 40px),1200px:calc(100% - 70px)" />

        <MegaMenu
            :model="hideNavItems ? [] : responsiveMenuItems"
            :pt="megaMenuPt"
            :breakpoint="mobileBreakpoint"
        >
            <template #menubuttonicon>
                <NavMenuButtonIcon />
            </template>

            <template #start>
                <router-link :to="{ name: 'home' }" class="pl-3" data-testid="nav-bar-home-link">
                    <Logo :show-without-text-on-mobile="true" />
                </router-link>
            </template>

            <template #item="{ item, props }">
                <NavMenuItem
                    v-if="!hideNavItems"
                    :item="item"
                    :props="props as MenuItemBindProps"
                />
            </template>

            <template #end>
                <NavBarActions />
            </template>
        </MegaMenu>
    </div>
</template>
